'use client'

import { useState, useEffect } from 'react'
import { Calendar } from 'lucide-react'

interface DeadlineBadgeProps {
  deadline: string
  /** 'pill' for detail page meta pill, 'inline' for card meta row */
  variant?: 'pill' | 'inline'
}

interface DeadlineInfo {
  label: string
  urgent: boolean
  /** True when the deadline is under 1 hour away - drives aria-live countdown. */
  countdown: boolean
}

function getDeadlineInfo(deadline: string): DeadlineInfo {
  const now = Date.now()
  const deadlineMs = new Date(deadline).getTime()
  const diff = deadlineMs - now

  if (diff <= 0) {
    // Job-detail page renders a separate closed-job banner; this is the
    // fallback label for inline meta rows.
    return { label: 'Closed', urgent: true, countdown: false }
  }

  const minutes = Math.floor(diff / 60_000)
  const hours = Math.floor(minutes / 60)
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24))

  if (minutes < 1) {
    return { label: 'Closing soon', urgent: true, countdown: true }
  }

  if (minutes < 60) {
    return {
      label: `Closes in ${minutes} minute${minutes === 1 ? '' : 's'}`,
      urgent: true,
      countdown: true,
    }
  }

  if (hours < 24) {
    return {
      label: `Closes in ${hours} hour${hours === 1 ? '' : 's'}`,
      urgent: true,
      countdown: false,
    }
  }

  if (days <= 7) {
    return {
      label: `Closes in ${days} day${days === 1 ? '' : 's'}`,
      urgent: true,
      countdown: false,
    }
  }

  const date = new Date(deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return { label: `Closes ${date}`, urgent: false, countdown: false }
}

export function DeadlineBadge({ deadline, variant = 'inline' }: DeadlineBadgeProps) {
  const [info, setInfo] = useState(() => getDeadlineInfo(deadline))

  // Schedule re-renders so the countdown stays accurate:
  //  - if >1h away: one-shot timeout to wake at the 1h boundary, then start
  //                 the 60s tick inline (no effect re-run needed)
  //  - if <=1h:     tick every 60s; recompute from wall clock (don't
  //                 decrement a counter - drift)
  useEffect(() => {
    const deadlineMs = new Date(deadline).getTime()
    let interval: ReturnType<typeof setInterval> | null = null
    let timeout: ReturnType<typeof setTimeout> | null = null

    const startInterval = () => {
      interval = setInterval(() => {
        setInfo(getDeadlineInfo(deadline))
      }, 60_000)
    }

    const diff = deadlineMs - Date.now()
    if (diff <= 0) return

    if (diff > 60 * 60 * 1000) {
      // Wake just after the 1-hour boundary, then begin per-minute updates.
      const wakeIn = diff - 60 * 60 * 1000 + 1_000
      timeout = setTimeout(() => {
        setInfo(getDeadlineInfo(deadline))
        startInterval()
      }, wakeIn)
    } else {
      startInterval()
    }

    return () => {
      if (interval) clearInterval(interval)
      if (timeout) clearTimeout(timeout)
    }
  }, [deadline])

  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }

  // aria-live announces updates only for the live countdown; non-urgent
  // "Closes {date}" labels are static and don't need to be polled by AT.
  const liveProps = info.countdown
    ? ({ 'aria-live': 'polite', 'aria-atomic': 'true', role: 'status' } as const)
    : {}

  if (variant === 'pill') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm ${
          info.urgent
            ? 'bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]'
            : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]'
        }`}
        style={font}
        {...liveProps}
      >
        <Calendar className="h-3.5 w-3.5" />
        {info.label}
      </span>
    )
  }

  return (
    <span
      className={`inline-flex items-center gap-1 ${
        info.urgent ? 'text-[var(--color-error)]' : ''
      }`}
      {...liveProps}
    >
      <Calendar className="h-3 w-3" />
      {info.label}
    </span>
  )
}
